/**
 * Hook bridging DosimetryStateService to React components.
 *
 * Follows the snapshot + event subscription pattern used by useSizingState / useStrategyState.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import type { IDoseTable } from '../domain/dose-table';
import type { IPerfusedVolume } from '../domain/perfused-volume';
import { DosimetryStateService, type SegmentEntry } from '../services/dosimetry-state-service';

import { useVisualization } from '@/context';

// ============================================================================
// Types
// ============================================================================

export type { SegmentEntry } from '../services/dosimetry-state-service';

export interface DosimetryStateSnapshot {
    perfusedVolumes: readonly IPerfusedVolume[];
    selectedPvId: string | null;
    selectedPerfusedVolume: IPerfusedVolume | undefined;
    segmentVolumes: ReadonlyMap<number, number>;
    tumorVolumes: ReadonlyMap<number, number>;
    segmentEntries: SegmentEntry[];
    tumorEntries: SegmentEntry[];
    totalLiverVolumeMl: number;
    hoveredStructure: string | null;
    selectedStructures: ReadonlySet<string>;
    adjustedDose: (pvId: string) => { injectedActivityGBq: number } | undefined;
    doseTable: (pvId: string) => IDoseTable | undefined;
    // Actions
    addPerfusedVolume: (
        segmentIndices: number[],
        tumorIndices: number[],
        name?: string
    ) => IPerfusedVolume | undefined;
    removePerfusedVolume: (id: string) => void;
    replacePerfusedVolume: (
        id: string,
        update: { segmentIndices: number[]; tumorIndices: number[]; name?: string }
    ) => IPerfusedVolume | undefined;
    selectPerfusedVolume: (id: string | null) => void;
    setAdjustedDose: (pvId: string, injectedActivityGBq: number) => void;
    clearAdjustedDose: (pvId: string) => void;
    setHovered: (key: string | null) => void;
    setSelectedStructures: (keys: ReadonlySet<string>) => void;
    clearHighlight: () => void;
    computeUnionVolumeMl: (segmentIndices: readonly number[], tumorIndices: readonly number[]) => number;
}

// ============================================================================
// Hook
// ============================================================================

export function useDosimetryState(): DosimetryStateSnapshot | null {
    const { getServiceManager } = useVisualization();
    const [snapshot, setSnapshot] = useState<DosimetryStateSnapshot | null>(null);
    const serviceRef = useRef<DosimetryStateService | null>(null);

    const actions = useMemo(
        () => ({
            addPerfusedVolume: (segmentIndices: number[], tumorIndices: number[], name?: string) =>
                serviceRef.current?.addPerfusedVolume(segmentIndices, tumorIndices, name),
            removePerfusedVolume: (id: string) => serviceRef.current?.removePerfusedVolume(id),
            replacePerfusedVolume: (
                id: string,
                update: { segmentIndices: number[]; tumorIndices: number[]; name?: string }
            ) => serviceRef.current?.replacePerfusedVolume(id, update),
            selectPerfusedVolume: (id: string | null) => serviceRef.current?.selectPerfusedVolume(id),
            setAdjustedDose: (pvId: string, injectedActivityGBq: number) =>
                serviceRef.current?.setAdjustedDose(pvId, injectedActivityGBq),
            clearAdjustedDose: (pvId: string) => serviceRef.current?.clearAdjustedDose(pvId),
            setHovered: (key: string | null) => serviceRef.current?.setHovered(key),
            setSelectedStructures: (keys: ReadonlySet<string>) =>
                serviceRef.current?.setSelectedStructures(keys),
            clearHighlight: () => serviceRef.current?.clearHighlight(),
        }),
        []
    );

    const buildSnapshot = useCallback(() => {
        const service = serviceRef.current;
        if (!service || !service.isInitialized()) {
            setSnapshot(null);
            return;
        }

        setSnapshot({
            perfusedVolumes: service.getPerfusedVolumes(),
            selectedPvId: service.getSelectedPvId(),
            selectedPerfusedVolume: service.getSelectedPerfusedVolume(),
            segmentVolumes: service.getSegmentVolumes(),
            tumorVolumes: service.getTumorVolumes(),
            segmentEntries: service.getSegmentEntries(),
            tumorEntries: service.getTumorEntries(),
            totalLiverVolumeMl: service.getTotalLiverVolumeMl(),
            hoveredStructure: service.getHoveredStructure(),
            selectedStructures: service.getSelectedStructures(),
            adjustedDose: (pvId: string) => service.getAdjustedDose(pvId),
            doseTable: (pvId: string) => service.getDoseTable(pvId),
            ...actions,
            computeUnionVolumeMl: (segmentIndices: readonly number[], tumorIndices: readonly number[]) =>
                service.computeUnionVolumeMl(segmentIndices, tumorIndices),
        });
    }, [actions]);

    useEffect(() => {
        const sm = getServiceManager();
        const service = sm.getService<DosimetryStateService>(DosimetryStateService.SERVICE_NAME);
        serviceRef.current = service;

        if (service.isInitialized()) {
            buildSnapshot();
        }

        const unsubscribers = service.ALL_EVENTS.map((event) => service.subscribe(event, buildSnapshot));

        return () => {
            serviceRef.current = null;
            unsubscribers.forEach((unsub) => unsub());
        };
    }, [getServiceManager, buildSnapshot]);

    return snapshot;
}
